import { TableHeader, TableHeaderIcons } from '@design/components/table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { flexRender, Header } from '@tanstack/react-table';
import { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import { TableAction } from '@terminal/views/screener/results/screener-table-utils';
import { TableDropdownMenu } from '@terminal/views/screener/results/table-dropdown-menu/TableDropdownMenu';
import clsx from 'clsx';
import { useState } from 'react';

import styles from './SortableTableHeaderCell.module.scss';

export interface SortableTableHeaderCellProps {
  enabledKeys?: TableAction[];
  activeFilterOptions?: ActiveFilterOptions[];
  enableDropdown?: boolean;
  handleAction: (action: TableAction) => void;
  header: Header<any, unknown>;
  alignment?: 'start' | 'center' | 'end';
}

export const SortableTableHeaderCell = ({
  enabledKeys = [],
  activeFilterOptions = [],
  enableDropdown = true,
  handleAction,
  header,
  alignment: initialAlignment,
}: SortableTableHeaderCellProps) => {
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);

  const isFiltered = header.column.getIsFiltered();
  const headerFilter = activeFilterOptions.find(f => f.filter === header.id);

  const columnMeta = (header.column.columnDef.meta ?? {}) as {
    alignment?: 'start' | 'center' | 'end';
    tooltipLabel?: string;
  };
  const alignment = initialAlignment ?? columnMeta?.alignment;

  if (enableDropdown) {
    const sortOrder = header.column.getIsSorted();

    return (
      <TableDropdownMenu
        onOpenChange={setIsDropdownMenuOpen}
        key={header.id}
        handleAction={handleAction}
        alignment={alignment}
        enabledKeys={enabledKeys}
        open={isDropdownMenuOpen}
        activeFilterDescription={
          headerFilter?.options[0].description ?? headerFilter?.options[0].name
        }
        renderTriggerNode={() => (
          <Tooltip
            inPortal
            label={columnMeta.tooltipLabel}
            trigger={TooltipTrigger.Hover}
            disabled={!columnMeta.tooltipLabel}
          >
            <TableHeader
              data-testid="table-header-sortable"
              data-sortable-header
              key={header.id}
              colSpan={header.colSpan}
              alignment={alignment}
              className={clsx(styles.styledTableHeader, {
                [styles.isOver]: isDropdownMenuOpen,
                [styles.hoverEnabled]: true,
                [styles.hasTooltip]: !!columnMeta?.tooltipLabel,
              })}
              onClick={() => {
                setIsDropdownMenuOpen(true);
              }}
            >
              <TableHeaderIcons
                alignment={alignment}
                isFiltered={isFiltered}
                sortDirection={sortOrder}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </TableHeaderIcons>
            </TableHeader>
          </Tooltip>
        )}
      />
    );
  }

  return (
    <TableHeader
      data-sortable-header
      key={header.id}
      colSpan={header.colSpan}
      alignment={alignment}
      className={styles.styledTableHeader}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
    </TableHeader>
  );
};
